// ID da empresa usado na geração das chaves LoRaWAN: é o 4º byte do DevEUI
// (4CFAC9 + ID + serial). Os firmwares BWS respondem 4CFAC901..., ou seja, 0x01.
export const LORAWAN_COMPANY_ID = 0x01;

export interface LorawanCredentials {
  device_address: string;
  device_eui: string;
  application_eui: string;
  application_key: string;
  application_session_key: string;
  network_session_key: string;
}

const KEY_IEEE = [
  0x4c, 0xfa, 0xc9, 0x4c, 0xfa, 0xc9, 0x4c, 0xfa, 0xc9, 0x4c, 0xfa, 0xc9, 0x4c, 0xfa, 0xc9, 0x4c,
];

const FIX_NWK = [
  0xaa, 0x2c, 0x00, 0x00, 0x87, 0x60, 0x00, 0x00, 0x00, 0x00, 0x23, 0x9d, 0xb1, 0xf5, 0x00, 0x00,
];

const FIX_APP = [
  0x55, 0x35, 0x00, 0x00, 0xcc, 0x27, 0x00, 0x00, 0x00, 0x00, 0x84, 0x51, 0xe3, 0x8b, 0x00, 0x00,
];

const FIX_APP_KEY = [
  0x76, 0x2d, 0x00, 0x00, 0x41, 0xe7, 0x00, 0x00, 0x00, 0x00, 0x9e, 0xf2, 0x53, 0x1c, 0x00, 0x00,
];

// Quebra um inteiro de 32 bits nos seus 4 bytes (big-endian).
function toBytes(value: number): number[] {
  return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
}

// Insere o serial e o timestamp nas posições reservadas do buffer base.
function applyData(base: number[], sn: number[], ts: number[]): number[] {
  const buffer = [...base];

  buffer[2] = sn[0];
  buffer[3] = sn[1];

  buffer[14] = sn[2];
  buffer[15] = sn[3];

  // Mantém a lógica original do algoritmo: só os bytes 1 e 2 do timestamp entram.
  buffer[6] = ts[1];
  buffer[7] = ts[2];
  buffer[8] = ts[1];
  buffer[9] = ts[2];

  return buffer;
}

function xor(a: number[], b: number[]): number[] {
  return a.map((value, index) => value ^ b[index]);
}

function toHex(data: number[]): string {
  return data
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Gera as credenciais LoRaWAN pela mesma fórmula do firmware, para conferir as
 * chaves lidas do dispositivo depois de gravar o serial e o timestamp.
 *
 * @param serialNumber - Serial gravado no dispositivo (inteiro de 32 bits).
 * @param timestamp - Timestamp gravado no dispositivo (inteiro de 32 bits).
 * @param companyId - ID da empresa, 4º byte do DevEUI (4CFAC9 + ID + serial).
 * @returns As chaves em hexadecimal maiúsculo.
 */
export function genLorawanCredentials(
  serialNumber: number,
  timestamp: number,
  companyId: number,
): LorawanCredentials {
  const sn = toBytes(serialNumber);
  const ts = toBytes(timestamp);

  const deviceEui = toHex([0x4c, 0xfa, 0xc9, companyId & 0xff, ...sn]);

  return {
    device_address: toHex(sn),
    device_eui: deviceEui,
    // AppEUI = DevEUI
    application_eui: deviceEui,
    application_key: toHex(xor(KEY_IEEE, applyData(FIX_APP_KEY, sn, ts))),
    application_session_key: toHex(xor(KEY_IEEE, applyData(FIX_APP, sn, ts))),
    network_session_key: toHex(xor(KEY_IEEE, applyData(FIX_NWK, sn, ts))),
  };
}

/**
 * Timestamp gravado junto com o serial (LWTK/WTK): epoch Unix em segundos, em HEX
 * maiúsculo. Precisa ser inteiro: o firmware guarda só 8 caracteres, então uma
 * fração ("68F62B75.FCAC08") voltaria truncada na leitura e a identificação reprovaria.
 */
export function lorawanTimestamp(nowMs: number = Date.now()): string {
  return Math.floor(nowMs / 1000)
    .toString(16)
    .toUpperCase();
}

export interface LorawanKeysCheck {
  /** Chaves geradas pela fórmula do firmware. */
  expected: LorawanCredentials;
  /** Chaves lidas do dispositivo, normalizadas para comparar com `expected`. */
  read: Partial<LorawanCredentials>;
  /** Todas as chaves foram lidas. */
  complete: boolean;
  /** Chaves que não batem com a fórmula (inclui as que não foram lidas). */
  mismatched: (keyof LorawanCredentials)[];
  valid: boolean;
}

/**
 * Confere as chaves lidas do dispositivo com as geradas pela fórmula do firmware a
 * partir do serial e do timestamp gravados.
 *
 * @param serial - Serial gravado, em HEX (ex.: "02010A16").
 * @param timestamp - Timestamp gravado, em HEX (ex.: "68F57A00").
 * @param read - Chaves lidas do dispositivo (ausentes quando não chegaram).
 * @param companyId - ID da empresa, 4º byte do DevEUI.
 */
export function verifyLorawanKeys(
  serial: string,
  timestamp: string,
  read: Partial<Record<keyof LorawanCredentials, string | undefined>>,
  companyId: number = LORAWAN_COMPANY_ID,
): LorawanKeysCheck {
  const expected = genLorawanCredentials(
    Number.parseInt(serial, 16),
    Number.parseInt(timestamp, 16),
    companyId,
  );

  const keys = Object.keys(expected) as (keyof LorawanCredentials)[];

  // O firmware responde em qualquer caixa e o device address (inteiro de 4 bytes,
  // como o serial) pode vir sem os zeros à esquerda.
  const normalized: Partial<LorawanCredentials> = {};
  for (const key of keys) {
    const value = read[key]?.toUpperCase();
    if (value) {
      normalized[key] = key === 'device_address' ? value.padStart(8, '0') : value;
    }
  }

  const complete = keys.every((key) => normalized[key] !== undefined);
  const mismatched = keys.filter((key) => normalized[key] !== expected[key]);

  return {
    expected,
    read: normalized,
    complete,
    mismatched,
    valid: complete && mismatched.length === 0,
  };
}
